import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  Upload,
  Plus,
  Trash2,
  CheckCircle2,
  XCircle,
  Loader2,
  AlertTriangle,
  FileText,
  Download,
  Info,
  ChevronDown,
} from "lucide-react";
import { API } from "@/lib/constants";
import {
  INDUSTRIES,
  CONTRACT_TYPES,
  MIN_WAGE,
  checkEligibility,
  daysBetween,
  computeEligibleDays,
  checkWage,
} from "@/data/visa88Days";
import { CONTRACT_LABELS, OFFICIAL_RULES } from "@/data/visaDaysI18n";
import { PostcodePicker } from "./PostcodePicker";
import { InlineLocationPicker } from "./InlineLocationPicker";

const STORAGE_KEY = "aussiecv_visa88_periods_v2";
const VISA_STORAGE_KEY = "aussiecv_visa88_visa_v1";
const TARGET_STORAGE_KEY = "aussiecv_visa88_target_v1";

function loadPeriods() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function savePeriods(p) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(p));
  } catch {
    // ignore
  }
}

const DEFAULT_FORM = {
  employer: "",
  postcode: "",
  industryKey: "plant_animal",
  start: "",
  end: "",
  contractType: "casual",
  daysWorked: "",
  hoursWorked: "",
  daysPerWeek: "5",
  hourlyRate: "",
};

export function Tracker88Days({ t, lang, user }) {
  const fileInputRef = useRef(null);

  const [visa, setVisa] = useState(() => localStorage.getItem(VISA_STORAGE_KEY) || "417");
  const [target, setTarget] = useState(() => parseInt(localStorage.getItem(TARGET_STORAGE_KEY) || "88", 10));
  const [periods, setPeriods] = useState(() => loadPeriods());
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [parsedToast, setParsedToast] = useState("");
  const [cloudSynced, setCloudSynced] = useState(false); // true once initial cloud load done

  const [showManual, setShowManual] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);

  const contractLabels = CONTRACT_LABELS[lang] || CONTRACT_LABELS.en;
  const rules = OFFICIAL_RULES[lang] || OFFICIAL_RULES.en;

  // Cloud sync: on login, fetch server data; on logout, revert to localStorage
  useEffect(() => {
    let cancelled = false;
    if (user) {
      (async () => {
        try {
          const res = await axios.get(`${API}/user/data`, { withCredentials: true });
          if (cancelled) return;
          const data = res.data || {};
          const serverPeriods = Array.isArray(data.periods) ? data.periods : [];
          // If server has data, use it (source of truth for logged-in users)
          if (serverPeriods.length > 0) {
            setPeriods(serverPeriods);
          }
          if (data.visa) setVisa(data.visa);
          if (data.target) setTarget(data.target);
        } catch {
          // fallback silently to localStorage
        } finally {
          if (!cancelled) setCloudSynced(true);
        }
      })();
    } else {
      setCloudSynced(false);
    }
    return () => { cancelled = true; };
  }, [user]);

  // Persist locally always (for anonymous users and as offline backup)
  useEffect(() => { savePeriods(periods); }, [periods]);
  useEffect(() => { localStorage.setItem(VISA_STORAGE_KEY, visa); }, [visa]);
  useEffect(() => { localStorage.setItem(TARGET_STORAGE_KEY, String(target)); }, [target]);

  // Cloud persist for logged-in users (after initial sync completed to avoid overwriting)
  useEffect(() => {
    if (!user || !cloudSynced) return;
    const timeout = setTimeout(() => {
      axios.post(
        `${API}/user/data`,
        { visa, target, periods },
        { withCredentials: true }
      ).catch(() => {});
    }, 600); // debounce
    return () => clearTimeout(timeout);
  }, [user, cloudSynced, visa, target, periods]);

  // Compute eligibility per period (postcode/industry/visa) + official day count + wage warning
  const enriched = useMemo(() => {
    return periods.map((p) => {
      const elig = checkEligibility({ postcode: p.postcode, visa, industryKey: p.industryKey });
      const calendarDays = daysBetween(p.start, p.end);
      const computedDays = elig.eligible ? computeEligibleDays(p) : 0;
      const wage = checkWage(p);
      return {
        ...p,
        calendarDays,
        countedDays: computedDays,
        eligible: elig.eligible,
        needsLocation: !!elig.needsLocation,
        reason: elig.reason,
        industry: elig.industry,
        wage,
      };
    });
  }, [periods, visa]);

  const totalEligible = enriched.reduce((s, p) => s + (p.eligible ? p.countedDays : 0), 0);
  const remaining = Math.max(0, target - totalEligible);
  const progressPct = Math.min(100, (totalEligible / target) * 100);
  const wageWarnings = enriched.filter((p) => p.wage);

  const addPeriod = (p) => setPeriods((cur) => [{ id: Date.now() + Math.random(), ...p }, ...cur]);
  const deletePeriod = (id) => setPeriods((cur) => cur.filter((p) => p.id !== id));
  const updatePeriod = (id, patch) =>
    setPeriods((cur) => cur.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const submitManual = (e) => {
    e.preventDefault();
    if (!form.employer || !form.postcode || !form.start || !form.end) return;
    addPeriod({
      ...form,
      source: "manual",
    });
    setForm(DEFAULT_FORM);
    setShowManual(false);
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    setParsedToast("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await axios.post(`${API}/parse-payslip`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const data = res.data || {};
      if (!data.start || !data.end) {
        setError(t.tracker.payslip_error);
        return;
      }
      // Build new period with whichever fields backend extracted
      const newPeriod = {
        employer: data.employer || "Unknown employer",
        postcode: data.postcode || "",
        industryKey: data.industryKey || "plant_animal",
        start: data.start,
        end: data.end,
        contractType: data.contractType || "casual",
        daysWorked: data.daysWorked ? String(data.daysWorked) : "",
        hoursWorked: data.hoursWorked ? String(data.hoursWorked) : "",
        daysPerWeek: "5",
        hourlyRate: data.hourlyRate ? String(data.hourlyRate) : "",
        source: "payslip",
        filename: file.name,
      };
      addPeriod(newPeriod);
      setParsedToast(t.tracker.payslip_parsed);
      setTimeout(() => setParsedToast(""), 3500);
    } catch (e) {
      // Surface backend detail message so the user knows what stage failed (read/OCR/AI)
      const detail = e?.response?.data?.detail;
      setError(detail || t.tracker.payslip_error);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const exportProofPDF = async () => {
    try {
      const res = await axios.post(`${API}/export-visa-proof`, {
        visa,
        periods: enriched.map((p) => ({
          employer: p.employer,
          postcode: p.postcode,
          industryKey: p.industryKey,
          start: p.start,
          end: p.end,
          days: p.countedDays,
          eligible: p.eligible,
        })),
        language: lang,
      }, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `88days_proof_${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError("Export failed");
    }
  };

  // Helper render: contract-type pill grid
  const ContractTypePills = ({ value, onChange }) => (
    <div className="grid grid-cols-2 gap-2">
      {CONTRACT_TYPES.map((ct) => {
        const active = value === ct.key;
        return (
          <button
            key={ct.key}
            type="button"
            onClick={() => onChange(ct.key)}
            className={`pill-card !text-left !items-start !py-2.5 ${active ? "active" : ""}`}
            data-testid={`contract-type-${ct.key}`}
          >
            <span className="text-lg mr-1">{ct.icon}</span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{contractLabels[ct.key]}</div>
              <div className="text-[10px] text-[#78716C] leading-tight mt-0.5">
                {t.tracker[`contract_${ct.key}_desc`]}
              </div>
            </div>
          </button>
        );
      })}
    </div>
  );

  // Helper render: inputs that depend on contract type
  const renderContractInputs = (f, setF) => {
    if (f.contractType === "casual" || f.contractType === "piece_rate") {
      return (
        <div>
          <label className="block mb-1 text-xs font-semibold text-[#292524]">{t.tracker.manual_days_worked}</label>
          <input
            type="number" min="0" inputMode="numeric"
            className="input"
            value={f.daysWorked}
            onChange={(e) => setF({ ...f, daysWorked: e.target.value })}
            data-testid="manual-days-worked"
          />
          <div className="text-[10px] text-[#78716C] mt-1">{t.tracker.manual_days_worked_hint}</div>
        </div>
      );
    }
    if (f.contractType === "part_time") {
      return (
        <div>
          <label className="block mb-1 text-xs font-semibold text-[#292524]">{t.tracker.manual_hours_worked}</label>
          <input
            type="number" min="0" step="0.25" inputMode="decimal"
            className="input"
            value={f.hoursWorked}
            onChange={(e) => setF({ ...f, hoursWorked: e.target.value })}
            data-testid="manual-hours-worked"
          />
          <div className="text-[10px] text-[#78716C] mt-1">{t.tracker.manual_hours_worked_hint}</div>
        </div>
      );
    }
    // fulltime
    return (
      <div>
        <label className="block mb-1 text-xs font-semibold text-[#292524]">{t.tracker.manual_days_per_week}</label>
        <select
          className="input"
          value={f.daysPerWeek}
          onChange={(e) => setF({ ...f, daysPerWeek: e.target.value })}
          data-testid="manual-days-per-week"
        >
          <option value="7">7 → 7</option>
          <option value="6">6 → 7</option>
          <option value="5">5 → 7</option>
          <option value="4">4 → 4</option>
          <option value="3">3 → 3</option>
        </select>
        <div className="text-[10px] text-[#78716C] mt-1">{t.tracker.manual_days_per_week_hint}</div>
      </div>
    );
  };

  return (
    <div className="max-w-5xl mx-auto" data-testid="tracker-88-days">
      <div className="mb-8">
        <h3 className="font-heading font-bold text-2xl lg:text-3xl text-[#292524] mb-2">
          {t.tracker.title}
        </h3>
        <p className="text-[#57534E]">{t.tracker.sub}</p>
        {user && cloudSynced && (
          <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#3B8268]/10 text-[#3B8268] text-xs font-semibold" data-testid="cloud-sync-badge">
            <CheckCircle2 className="w-3 h-3" />
            {lang === "fr" ? "Synchronisé dans le cloud" : lang === "de" ? "Cloud-synchronisiert" : lang === "it" ? "Sincronizzato nel cloud" : lang === "es" ? "Sincronizado en la nube" : lang === "pt" ? "Sincronizado na cloud" : "Cloud synced"}
          </div>
        )}
      </div>

      {/* Visa + Target row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="card">
          <label className="block mb-3 font-semibold text-[#292524] text-sm">{t.tracker.visa_select}</label>
          <div className="grid grid-cols-2 gap-2">
            {["417", "462"].map((v) => (
              <button key={v} onClick={() => setVisa(v)}
                className={`pill-card !justify-center !text-center ${visa === v ? "active" : ""}`}
                data-testid={`tracker-visa-${v}`}>
                <span className="font-semibold">{v} WHV</span>
              </button>
            ))}
          </div>
        </div>
        <div className="card">
          <label className="block mb-3 font-semibold text-[#292524] text-sm">{t.tracker.target_select}</label>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => setTarget(88)}
              className={`pill-card !justify-center !text-center ${target === 88 ? "active" : ""}`}
              data-testid="tracker-target-88">
              <span className="font-semibold text-sm">{t.tracker.target_88}</span>
            </button>
            <button onClick={() => setTarget(179)}
              className={`pill-card !justify-center !text-center ${target === 179 ? "active" : ""}`}
              data-testid="tracker-target-179">
              <span className="font-semibold text-sm">{t.tracker.target_179}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Progress card */}
      <div className="card mb-6 !p-5 sm:!p-6" data-testid="tracker-progress">
        {/* Title + intro */}
        <div className="mb-4">
          <div className="overline mb-1.5">{t.tracker.progress_title}</div>
          <p
            className="text-sm text-[#57534E] leading-snug"
            dangerouslySetInnerHTML={{ __html: t.tracker.progress_intro }}
          />
        </div>

        {/* Big number + remaining */}
        <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
          <div>
            <div className="overline mb-1 !text-[#78716C]">{t.tracker.progress_label}</div>
            <div className="flex items-baseline gap-2">
              <span
                className="font-heading font-extrabold text-5xl leading-none"
                style={{ color: totalEligible >= target ? "#3B8268" : "#292524" }}
                data-testid="tracker-total-eligible"
              >
                {totalEligible}
              </span>
              <span className="text-[#78716C] font-semibold text-lg">/ {target} {t.tracker.stat_days}</span>
            </div>
          </div>
          {totalEligible >= target ? (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#3B8268]/10 text-[#3B8268] font-bold text-sm">
              <CheckCircle2 className="w-4 h-4" /> {t.tracker.complete}
            </div>
          ) : (
            <div className="text-right">
              <span className="font-heading font-extrabold text-2xl text-[#D95D39]">{remaining}</span>
              <div className="text-xs text-[#78716C] font-semibold">{t.tracker.remaining}</div>
            </div>
          )}
        </div>

        {/* Progress bar */}
        <div className="h-3 rounded-full bg-[#EAE5DB] overflow-hidden mb-4">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="h-full bg-gradient-to-r from-[#E5A93D] to-[#3B8268]"
          />
        </div>

        {/* Stat pills — visible only when there are periods */}
        {enriched.length > 0 && (() => {
          const validated = enriched.filter((p) => p.eligible).length;
          const toComplete = enriched.filter((p) => p.needsLocation).length;
          const rejected = enriched.filter((p) => !p.eligible && !p.needsLocation).length;
          return (
            <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4" data-testid="tracker-stat-pills">
              <div className="rounded-xl border border-[#D6EDDD] bg-[#F1F9F3] px-3 py-2 text-center">
                <div className="text-xl sm:text-2xl font-heading font-extrabold text-[#3B8268]" data-testid="stat-validated-count">{validated}</div>
                <div className="text-[10px] sm:text-xs text-[#3B8268] font-semibold leading-tight mt-0.5">{t.tracker.stat_validated}</div>
              </div>
              <div className="rounded-xl border border-[#E5A93D]/40 bg-[#FDF7E6] px-3 py-2 text-center">
                <div className="text-xl sm:text-2xl font-heading font-extrabold text-[#8B6A1E]" data-testid="stat-tocomplete-count">{toComplete}</div>
                <div className="text-[10px] sm:text-xs text-[#8B6A1E] font-semibold leading-tight mt-0.5">{t.tracker.stat_to_complete}</div>
              </div>
              <div className="rounded-xl border border-[#F5CFCE] bg-[#FEF2F2] px-3 py-2 text-center">
                <div className="text-xl sm:text-2xl font-heading font-extrabold text-[#D9423E]" data-testid="stat-rejected-count">{rejected}</div>
                <div className="text-[10px] sm:text-xs text-[#D9423E] font-semibold leading-tight mt-0.5">{t.tracker.stat_rejected}</div>
              </div>
            </div>
          );
        })()}

        {/* Export button + explanation */}
        {enriched.length > 0 && (
          <div className="pt-2 border-t border-[#F2EBE1]">
            <button onClick={exportProofPDF} className="btn-outline !py-2 text-sm mt-3" data-testid="tracker-export-btn">
              <Download className="w-4 h-4" /> {t.tracker.export_btn}
            </button>
            <p className="text-xs text-[#78716C] mt-2 leading-snug">
              {t.tracker.export_hint}
            </p>
          </div>
        )}
      </div>

      {/* Wage warning banner */}
      {wageWarnings.length > 0 && (
        <div className="card mb-6 !border-[#D9423E] !bg-[#FDEEEC] !border-2" data-testid="wage-warnings">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#D9423E] flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-heading font-bold text-[#9B3B1E] mb-1">{t.tracker.wage_warning_title}</div>
              {wageWarnings.map((p) => (
                <div key={p.id} className="text-sm text-[#57534E] mb-1">
                  <strong>{p.employer}</strong> — {t.tracker.wage_warning_body
                    .replace("{rate}", p.wage.rate.toFixed(2))
                    .replace("{minimum}", p.wage.minimum.toFixed(2))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Official rules — always visible */}
      <div className="card mb-6 !p-5" data-testid="rules-panel">
        <div className="flex items-center gap-2 mb-3">
          <Info className="w-4 h-4 text-[#2A5C5A]" />
          <div className="font-heading font-bold text-[#292524]">{rules.title}</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 text-sm">
          <div>
            <div className="font-semibold text-[#D95D39] mb-2">{rules.casual}</div>
            <table className="w-full text-xs">
              <tbody>
                {rules.table_hours.map(([l, r], i) => (
                  <tr key={i} className="border-b border-[#F2EBE1] last:border-0">
                    <td className="py-1 text-[#57534E]">{l}</td>
                    <td className="py-1 text-[#292524] font-semibold text-right">{r}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <div className="font-semibold text-[#2A5C5A] mb-2">{rules.fulltime}</div>
            <table className="w-full text-xs">
              <tbody>
                {rules.table_full.map(([l, r], i) => (
                  <tr key={i} className="border-b border-[#F2EBE1] last:border-0">
                    <td className="py-1 text-[#57534E]">{l}</td>
                    <td className="py-1 text-[#292524] font-semibold text-right">{r}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="mt-4 text-xs text-[#9B3B1E] italic">⚠️ {rules.warning}</div>
      </div>

      {/* Upload + manual add */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="card">
          <div className="font-heading font-semibold text-lg text-[#292524] mb-1">{t.tracker.upload_title}</div>
          <p className="text-sm text-[#78716C] mb-3">{t.tracker.upload_sub}</p>
          <input ref={fileInputRef} type="file" accept=".pdf,image/*" className="hidden"
            onChange={(e) => onUpload(e.target.files?.[0])} data-testid="tracker-payslip-input" />
          <button type="button" className="btn-primary w-full"
            onClick={() => fileInputRef.current?.click()} disabled={uploading}
            data-testid="tracker-payslip-upload-btn">
            {uploading ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> {t.tracker.uploading}</>
            ) : (
              <><Upload className="w-4 h-4" /> {t.tracker.upload_btn}</>
            )}
          </button>
          {parsedToast && (
            <div className="mt-3 text-xs text-[#3B8268] flex items-center gap-1.5" data-testid="tracker-parsed-toast">
              <CheckCircle2 className="w-3.5 h-3.5" /> {parsedToast}
            </div>
          )}
          {error && (
            <div className="mt-3 text-xs text-[#D9423E] flex items-center gap-1.5" data-testid="tracker-error">
              <AlertTriangle className="w-3.5 h-3.5" /> {error}
            </div>
          )}
        </div>

        <div className="card">
          <div className="font-heading font-semibold text-lg text-[#292524] mb-1">{t.tracker.add_manual}</div>
          <p className="text-sm text-[#78716C] mb-3">&nbsp;</p>
          <button type="button" className="btn-outline w-full"
            onClick={() => setShowManual((s) => !s)} data-testid="tracker-manual-toggle">
            <Plus className="w-4 h-4" /> {t.tracker.manual_add}
          </button>
        </div>
      </div>

      {/* Manual form */}
      {showManual && (
        <form onSubmit={submitManual} className="card mb-6 space-y-4" data-testid="tracker-manual-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block mb-1 text-xs font-semibold text-[#292524]">{t.tracker.manual_employer}</label>
              <input className="input" value={form.employer}
                onChange={(e) => setForm({ ...form, employer: e.target.value })}
                data-testid="manual-employer" />
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-[#292524]">{t.tracker.manual_postcode}</label>
              <PostcodePicker value={form.postcode}
                onChange={(v) => setForm({ ...form, postcode: v })} placeholder="6714" />
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-[#292524]">{t.tracker.manual_start}</label>
              <input type="date" className="input" value={form.start}
                onChange={(e) => setForm({ ...form, start: e.target.value })}
                data-testid="manual-start" />
            </div>
            <div>
              <label className="block mb-1 text-xs font-semibold text-[#292524]">{t.tracker.manual_end}</label>
              <input type="date" className="input" value={form.end}
                onChange={(e) => setForm({ ...form, end: e.target.value })}
                data-testid="manual-end" />
            </div>
          </div>

          <div>
            <label className="block mb-1 text-xs font-semibold text-[#292524]">{t.tracker.manual_industry}</label>
            <select className="input" value={form.industryKey}
              onChange={(e) => setForm({ ...form, industryKey: e.target.value })}
              data-testid="manual-industry">
              {INDUSTRIES.map((ind) => (
                <option key={ind.key} value={ind.key}>
                  {ind.icon} {ind.label[lang] || ind.label.en}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block mb-2 text-xs font-semibold text-[#292524]">{t.tracker.manual_contract}</label>
            <ContractTypePills value={form.contractType}
              onChange={(v) => setForm({ ...form, contractType: v })} />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {renderContractInputs(form, setForm)}
            <div>
              <label className="block mb-1 text-xs font-semibold text-[#292524]">{t.tracker.manual_hourly_rate}</label>
              <input type="number" min="0" step="0.01" inputMode="decimal"
                className="input"
                value={form.hourlyRate}
                onChange={(e) => setForm({ ...form, hourlyRate: e.target.value })}
                data-testid="manual-hourly-rate" />
              <div className="text-[10px] text-[#78716C] mt-1">{t.tracker.manual_hourly_rate_hint}</div>
            </div>
          </div>

          <button type="submit" className="btn-primary" data-testid="manual-submit">
            <Plus className="w-4 h-4" /> {t.tracker.manual_add}
          </button>
        </form>
      )}

      {/* Periods table */}
      {enriched.length === 0 ? (
        <div className="card text-center py-12" data-testid="tracker-empty">
          <FileText className="w-10 h-10 text-[#D95D39] mx-auto mb-3" />
          <div className="font-heading font-semibold text-lg text-[#292524] mb-1">{t.tracker.empty_title}</div>
          <p className="text-sm text-[#78716C]">{t.tracker.empty_sub}</p>
        </div>
      ) : (
        <div className="card !p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="tracker-table">
              <thead>
                <tr className="bg-[#FAF6F0] border-b border-[#EAE5DB]">
                  <th className="text-left px-4 py-3 font-semibold text-[#292524]">{t.tracker.th_period}</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#292524]">{t.tracker.th_days}</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#292524]">{t.tracker.th_employer}</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#292524]">{t.tracker.th_location}</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#292524]">{t.tracker.manual_contract}</th>
                  <th className="text-left px-4 py-3 font-semibold text-[#292524]">{t.tracker.th_status}</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {enriched.map((p) => {
                  const ct = CONTRACT_TYPES.find((c) => c.key === p.contractType);
                  return (
                    <tr key={p.id} className="border-b border-[#F2EBE1] last:border-0 hover:bg-[#FAF6F0]/50" data-testid={`tracker-row-${p.id}`}>
                      <td className="px-4 py-3 text-[#57534E] whitespace-nowrap text-xs">{p.start} → {p.end}</td>
                      <td className="px-4 py-3">
                        <span className="font-bold text-[#292524]">{p.countedDays}</span>
                        <span className="text-[#A8A29E] text-xs"> / {p.calendarDays}</span>
                      </td>
                      <td className="px-4 py-3 text-[#292524] text-xs">
                        {p.employer}
                        {p.wage && (
                          <div className="text-[10px] text-[#D9423E] font-semibold mt-0.5 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3" /> ${p.wage.rate.toFixed(2)}/h &lt; ${p.wage.minimum.toFixed(2)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs">
                        <InlineLocationPicker
                          value={p.postcode}
                          onChange={(newPc) => updatePeriod(p.id, { postcode: newPc })}
                          labelChoose={t.tracker.choose_location}
                          labelChange={t.tracker.change_location}
                          hint={t.tracker.location_missing_hint}
                          testIdPrefix={`loc-${p.id}`}
                        />
                      </td>
                      <td className="px-4 py-3 text-[#57534E] text-xs">
                        {ct ? `${ct.icon} ${contractLabels[ct.key]}` : "—"}
                      </td>
                      <td className="px-4 py-3">
                        {p.eligible ? (
                          <span className="inline-flex items-center gap-1 text-[#3B8268] font-semibold text-xs" data-testid={`status-eligible-${p.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5" /> {t.tracker.eligible}
                          </span>
                        ) : p.needsLocation ? (
                          <span className="inline-flex items-center gap-1 text-[#8B6A1E] bg-[#FDF7E6] border border-[#E5A93D] rounded-md px-1.5 py-0.5 font-semibold text-xs" data-testid={`status-location-required-${p.id}`}>
                            <AlertTriangle className="w-3.5 h-3.5" /> {t.tracker.location_required}
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-[#D9423E] font-semibold text-xs" data-testid={`status-not-eligible-${p.id}`}>
                            <XCircle className="w-3.5 h-3.5" /> {t.tracker.not_eligible}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => deletePeriod(p.id)}
                          className="text-[#78716C] hover:text-[#D9423E] transition-colors"
                          data-testid={`tracker-delete-${p.id}`} aria-label="Delete">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
